// Copyright (c) StrangeBee 2023

import React, { ReactElement, useState } from 'react';
import { Input, Form } from 'antd';
import { Store } from 'antd/lib/form/interface';
import { useLocalMessage } from '@Helpers/';
import { useHistory } from 'react-router-dom';
import RouteBuilder from '@Services/RouteBuilder';
import { ICONS } from '@Constants/iconsApp';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { MFA_LENGTH, MFA_RULES } from '@Constants/mfa';

import './LoginForm.scss';

interface LoginFormProps {
    onFinish?(values: Store): void;
    mfa?: boolean;
}

const LoginForm = ({ onFinish, mfa }: LoginFormProps): ReactElement => {
    const i = useLocalMessage();
    const history = useHistory();

    const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);

    const onValuesChanged = (_: Store, allValues: Store): void => {
        // Should be enabled only if login field is not empty
        // And if password field is not empty
        const shouldSubmitButtonBeEnabled = allValues['login'] && allValues['password'];
        // Set disabled props
        setIsButtonDisabled(!shouldSubmitButtonBeEnabled);
    };

    return (
        <Form name="loginForm" onFinish={onFinish} onValuesChange={onValuesChanged} className="login-form">
            <Form.Item
                name="login"
                rules={[
                    {
                        type: 'string',
                        required: true,
                        message: i('login.form.login.required'),
                    },
                ]}
            >
                <Input
                    placeholder={i('login.form.login.placeholder')}
                    className="login-form__item"
                    prefix={<ICONS.UserIcon className="icon" />}
                    data-testid="login"
                    disabled={mfa}
                />
            </Form.Item>
            <Form.Item name="password" rules={[{ required: true, message: i('login.form.password.required') }]}>
                <Input.Password
                    placeholder={i('login.form.password.placeholder')}
                    className="login-form__item"
                    prefix={<ICONS.LockIcon className="icon" />}
                    data-testid="password"
                    disabled={mfa}
                />
            </Form.Item>
            {mfa && (
                <Form.Item
                    name="code"
                    rules={[
                        // Required
                        {
                            required: true,
                            message: i('login.form.code.required'),
                        },
                        // Other rules
                        ...MFA_RULES(i),
                    ]}
                >
                    <Input
                        placeholder={i('login.form.code.placeholder')}
                        className="login-form__item"
                        prefix={<ICONS.LockIcon className="icon" />}
                        data-testid="code"
                        maxLength={MFA_LENGTH}
                    />
                </Form.Item>
            )}

            <div className="login-form__actions">
                <div className="form-actions__forgot">
                    <THButton
                        type="link"
                        className="form-actions__button"
                        onClick={(): void => history.push(RouteBuilder.compute('login.reset'))}
                    >
                        {i('login.form.actions.forgot')}
                    </THButton>
                </div>
                <div className="form-actions__signin">
                    <THButton
                        block
                        type="primary"
                        className="form-actions__button"
                        htmlType="submit"
                        disabled={isButtonDisabled}
                        role="submit"
                    >
                        {i('login.form.actions.signin')}
                    </THButton>
                </div>
            </div>
        </Form>
    );
};

export default LoginForm;
